import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  ShieldCheck, 
  Check, 
  ArrowLeft, 
  RotateCcw, 
  Code, 
  CheckCircle2, 
  AlertTriangle,
  XCircle,
  Gauge,
  Sparkles
} from 'lucide-react';

export const SEOAnalyzer = () => {
  const { withAd, toast } = useAdRedirect();
  const [htmlInput, setHtmlInput] = useState(`<!DOCTYPE html>
<html>
<head>
  <title>NextGen SaaS Management Platform - Fast & Secure</title>
  <meta name="description" content="Manage your entire cloud infrastructure with our premium dashboard. Secure, automated, and built for modern development teams.">
</head>
<body>
  <h1>NextGen Cloud Management Platform</h1>
  <h2>Core Platform Features</h2>
  <p>Our dashboard integrates with AWS, GCP, and Azure instantly.</p>
  <img src="dashboard-preview.png" alt="Cloud Dashboard UI Mockup">
  <h2>Enterprise Security Standards</h2>
  <p>We comply with SOC2, ISO27001, and GDPR strict mandates.</p>
</body>
</html>`);

  // Parse HTML string client-side using DOMParser
  const analyzeSEO = () => {
    const raw = htmlInput.trim();
    if (!raw) return { score: 0, checks: [] };

    let parser = new DOMParser();
    let doc = parser.parseFromString(raw, 'text/html');

    let checks = [];
    let passedCount = 0;
    let totalChecks = 5;

    // 1. Title Tag
    const titleElement = doc.querySelector('title');
    const titleText = titleElement ? titleElement.textContent.trim() : '';
    if (titleText) {
      if (titleText.length >= 30 && titleText.length <= 65) {
        checks.push({ name: 'Title Tag Length', status: 'pass', value: `"${titleText}" (${titleText.length} chars)`, msg: 'Optimal length for Google SERP display.' });
        passedCount++;
      } else {
        checks.push({ name: 'Title Tag Length', status: 'warn', value: `"${titleText}" (${titleText.length} chars)`, msg: 'Title should be between 30 and 65 characters.' });
        passedCount += 0.5;
      }
    } else {
      checks.push({ name: 'Title Tag Presence', status: 'fail', value: 'Missing <title> tag', msg: 'Every webpage requires a title tag.' });
    }

    // 2. Meta Description
    const metaDesc = doc.querySelector('meta[name="description"]');
    const descText = metaDesc ? metaDesc.getAttribute('content') || '' : '';
    if (descText) {
      if (descText.length >= 120 && descText.length <= 165) {
        checks.push({ name: 'Meta Description', status: 'pass', value: `"${descText.substring(0, 50)}..." (${descText.length} chars)`, msg: 'Optimal length for search snippets.' });
        passedCount++;
      } else {
        checks.push({ name: 'Meta Description', status: 'warn', value: `Length: ${descText.length} chars`, msg: 'Description should be between 120 and 165 characters.' });
        passedCount += 0.5;
      }
    } else {
      checks.push({ name: 'Meta Description', status: 'fail', value: 'Missing <meta name="description">', msg: 'Crucial for search CTR rates.' });
    }

    // 3. H1 Tag
    const h1Tags = doc.querySelectorAll('h1');
    if (h1Tags.length === 1) {
      checks.push({ name: 'H1 Heading Tag', status: 'pass', value: `"${h1Tags[0].textContent.trim()}"`, msg: 'Exactly one H1 tag present.' });
      passedCount++;
    } else if (h1Tags.length > 1) {
      checks.push({ name: 'H1 Heading Tag', status: 'warn', value: `Found ${h1Tags.length} H1 tags`, msg: 'Use exactly one H1 tag per webpage for clean structure.' });
      passedCount += 0.5;
    } else {
      checks.push({ name: 'H1 Heading Tag', status: 'fail', value: 'Missing <h1> tag', msg: 'Search engines rely on H1 tags for topical indexing.' });
    }

    // 4. Image Alt Attributes
    const images = doc.querySelectorAll('img');
    let imgTotal = images.length;
    let imgWithAlt = 0;
    images.forEach(img => {
      if (img.hasAttribute('alt') && img.getAttribute('alt').trim() !== '') {
        imgWithAlt++;
      }
    });

    if (imgTotal === 0) {
      checks.push({ name: 'Image Alt Tags', status: 'pass', value: 'No images found', msg: 'No alt attributes needed.' });
      passedCount++;
    } else if (imgTotal === imgWithAlt) {
      checks.push({ name: 'Image Alt Tags', status: 'pass', value: `${imgWithAlt} / ${imgTotal} images optimized`, msg: 'All images contain descriptive alt text.' });
      passedCount++;
    } else {
      checks.push({ name: 'Image Alt Tags', status: 'fail', value: `${imgWithAlt} / ${imgTotal} images optimized`, msg: 'Some images lack alt attributes.' });
    }

    // 5. Word Count / Sufficiency
    const bodyText = doc.body ? doc.body.textContent.trim() : '';
    const wordCount = bodyText.split(/\s+/).filter(Boolean).length;
    if (wordCount > 30) {
      checks.push({ name: 'Body Word Count', status: 'pass', value: `${wordCount} words`, msg: 'Sufficient content for basic semantic indexing.' });
      passedCount++;
    } else {
      checks.push({ name: 'Body Word Count', status: 'warn', value: `${wordCount} words`, msg: 'Thin content detected. Aim for at least 300 words.' });
      passedCount += 0.5;
    }

    const score = Math.round((passedCount / totalChecks) * 100);
    return { score, checks };
  };

  const audit = analyzeSEO();

  const handleReset = () => {
    setHtmlInput('');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free SEO Analyzer & Page Audit Score" 
        description="Audit your website content, headings, meta descriptions, and image alt tags instantly. Get an actionable SEO optimization score."
        keywords="seo analyzer, website audit score, page audit, on-page seo checker, heading structure analyzer, seo scoring tool"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>SEO Analyzer</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          On-Page SEO Analyzer & Audit Score
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Perform a real-time client-side DOM audit of your HTML structure to ensure full compliance with Google Core Web Vitals and semantic standards.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Input Text */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Code size={18} style={{ color: 'var(--primary)' }} /> Paste Raw HTML Markup Code
              </h3>
              <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RotateCcw size={14} /> Clear Markup
              </button>
            </div>

            <div>
              <textarea 
                rows="18"
                value={htmlInput} 
                onChange={(e) => setHtmlInput(e.target.value)}
                placeholder="Paste your webpage HTML source code here..."
                style={{ width: '100%', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', fontSize: '0.85rem', fontFamily: 'monospace', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6 }}
              />
            </div>
          </div>

        </div>

        {/* Right Side: Score & Breakdown */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', paddingBottom: '20px' }}>
              <div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Gauge size={18} style={{ color: '#10b981' }} /> Page Optimization Score
                </h3>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Calculated via client-side DOM parser</span>
              </div>

              <div style={{ 
                width: '72px', 
                height: '72px', 
                borderRadius: '36px', 
                background: audit.score >= 80 ? 'rgba(16, 185, 129, 0.1)' : audit.score >= 50 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: `3px solid ${audit.score >= 80 ? '#10b981' : audit.score >= 50 ? '#f59e0b' : '#ef4444'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.4rem',
                fontWeight: 800,
                color: audit.score >= 80 ? '#10b981' : audit.score >= 50 ? '#f59e0b' : '#ef4444'
              }}>
                {audit.score}
              </div>
            </div>

            {/* Audit Rule Checks */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {audit.checks.map((chk, idx) => (
                <div key={idx} style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                  <div style={{ marginTop: '2px' }}>
                    {chk.status === 'pass' && <CheckCircle2 size={20} style={{ color: '#10b981' }} />}
                    {chk.status === 'warn' && <AlertTriangle size={20} style={{ color: '#f59e0b' }} />}
                    {chk.status === 'fail' && <XCircle size={20} style={{ color: '#ef4444' }} />}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary)' }}>{chk.name}</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: '6px', background: chk.status === 'pass' ? 'rgba(16, 185, 129, 0.1)' : chk.status === 'warn' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: chk.status === 'pass' ? '#10b981' : chk.status === 'warn' ? '#f59e0b' : '#ef4444' }}>
                        {chk.status.toUpperCase()}
                      </span>
                    </div>

                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'monospace', background: 'rgba(15, 23, 42, 0.04)', padding: '4px 8px', borderRadius: '6px' }}>
                      {chk.value}
                    </div>

                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                      {chk.msg}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <AdToast message={toast} />
              <button 
                onClick={withAd('seo-analyzer-btn', () => alert("Audit Complete! All client-side checks updated."))}
                style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '12px 28px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', transition: 'var(--transition-smooth)' }}
              >
                Re-Run Full Audit
              </button>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="seo-analyzer" />
    </div>
  );
};

export default SEOAnalyzer;
